import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, OnDestroy, ViewChild,
  ElementRef,
  SimpleChanges, } from '@angular/core';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { Action, Store } from '@ngrx/store';
import { Observable, of, Subject, Subscription } from 'rxjs';
import {
  DnvFeatureLayer, DnvMapState, LayerState, LayerType, SetHighlightFeature, ToggleFeaturePanel, Layer as DnvLayer,
  SetSelectedFeatureLayer, FeatureClicked, DnvLatLng
} from 'dnv-lib';
import { Drawing, EnergovInfo, PropertyInfo, PropertyOwner } from '../../state-management';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
// import { environment } from 'src/environments/environment';
import { debounceTime, delay, distinctUntilChanged, mergeMap, switchMap } from 'rxjs/operators';

class StringValue {
  type: string;
  text: string;
  value: string;
}
function newStringValue(value: string) {
  return {
    type: 'string',
    text: (value || '').toLowerCase(),
    value: value
  };
}
class LinkValue extends StringValue {
  url: string;
  small: string;
}
function newLinkValue(url: string, value: string, small: string = null) {
  return {
    type: 'link',
    text: (value || '').toLowerCase() + (small || '').toLowerCase(),
    url: url,
    value: value,
    small: small
  };
}
class ButtonValue extends StringValue {
  drawingNumber: string;
  drawingType: string;
}
function newButtonValue(drawingNumber: string, drawingType: string, value: string) {
  return {
    type: 'button',
    text: (value || '').toLowerCase(),
    drawingNumber: drawingNumber,
    drawingType: drawingType,
    value: value
  };
}
class Pinfo {
  key: string;
  values: StringValue[];
  text: string;
  children: Pinfo[];
}

function newPinfo(key: string, values: StringValue[], children: Pinfo[]): Pinfo {
  const pInfo = new Pinfo();
  pInfo.key = key;
  pInfo.values = values;
  pInfo.text = key.toLowerCase() + values.reduce((accumulator: string, currentValue: any) => accumulator + currentValue.text, '');
  pInfo.children = children;
  return pInfo;
}

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'dnv-property-info',
    templateUrl: './dnv-property-info.component.html',
    styleUrls: ['./dnv-property-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DnvPropertyInfoComponent implements OnInit, OnChanges, OnDestroy {

  @Input() propertyInfo: PropertyInfo[];
  @Input() selectedProperty: string;
  @Input() headerHeight: number;
  @Input() panelHeight: number;
  @Input() opacity: number;
  @Input() appSettings: any;
  @Input() energovInfo: EnergovInfo;
  @Input() scrollToTop: boolean = false;

  @Output() closePanel = new EventEmitter();
  @Output() openDrawingEmitter = new EventEmitter<{ drawNum: string, drawType: string }>();
  @Output() backToMultiEmitter = new EventEmitter<string>();

  @ViewChild('propertyDetails') public propertyDetails: ElementRef | undefined;

  scrollHeight: number;

  currentLatLng: DnvLatLng;
  streetViewStatic: string;

  filter: string;
  fullPropertyInfo: Pinfo[];
  filteredPropertyInfo: Pinfo[];
  searchPropertyInformationForm: UntypedFormGroup;
  keyUp = new Subject<KeyboardEvent>();
  keyUpSubscription: Subscription;
  fullCasesInfo: Pinfo[];

  constructor(
    private sanitizer: DomSanitizer,
    private decimalPipe: DecimalPipe,
    private currencyPipe: CurrencyPipe) {

    this.searchPropertyInformationForm = new UntypedFormGroup({
      searchPropertyInformation: new UntypedFormControl('')
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.scrollHeight) {
      this.scrollHeight = this.panelHeight - this.headerHeight + 10;
    }

    if (this.currentLatLng) {
      if (this.propertyInfo[0].properties[0].details.geography.latitude !== this.currentLatLng.lat
        && this.propertyInfo[0].properties[0].details.geography.longitude !== this.currentLatLng.lat) {

        this.streetViewStatic = this.getStreetViewStatic(this.propertyInfo[0].properties[0].details.geography);
      }
    }

    if (this.propertyInfo[0]) {
      this.parsePropertyInfo();
      this.filterPropertyInfo();
      this.parseCases();
    }

    if (changes['propertyInfo']) {
      if (this.scrollToTop === true) {
        this.propertyDetails?.nativeElement.scrollTo(0, 0);
      }
  
    }

  }

  ngOnInit(): void {
    if (this.propertyInfo[0]) {
      this.streetViewStatic = this.getStreetViewStatic(this.propertyInfo[0].properties[0].details.geography);
    }

    this.keyUpSubscription = this.keyUp
      .subscribe((data) => {
        this.filterPropertyInfo();
      });
  }

  ngOnDestroy(): void {
    this.keyUpSubscription.unsubscribe();
  }

  parsePropertyInfo(): void {
    const na = 'N/A';
    const yes = 'YES';
    const no = 'NO';
    const pi: Pinfo[] = [];


    // Location
    const locationChildren: Pinfo[] = [];
    if (this.propertyInfo[0].properties[0].addresses.length === 0) {
      locationChildren.push(newPinfo('Mailing Address', [newStringValue('No address available')], []));
    } else {
      locationChildren.push(newPinfo('Mailing Address'
        , [
          newStringValue(`${this.propertyInfo[0].properties[0].addresses[0].fullAddress}<br />North Vancouver, BC ${this.propertyInfo[0].properties[0].addresses[0].postCode || ''}`) // Because postCode can be null and gets rendered as 'null'
        ]
        , []));
    }

    locationChildren.push(newPinfo('Legal Description'
      , [
        newStringValue(this.propertyInfo[0].properties[0].details.identification.legalDescription || na)
      ]
      , []));

    locationChildren.push(newPinfo('LTO PID'
      , [
        newStringValue(this.propertyInfo[0].properties[0].details.identification.ltopid || na)
      ]
      , []));

    locationChildren.push(newPinfo('Folio'
      , [
        newStringValue(this.propertyInfo[0].properties[0].details.identification.folio || na)
      ]
      , []));

    locationChildren.push(newPinfo('DNV Parcel ID'
      , [
        newStringValue(this.propertyInfo[0].properties[0].details.identification.dbLink)
      ]
      , []));

    pi.push(newPinfo('LocationKey', [newStringValue('Location')], locationChildren));

    // Site
    const siteChildren: Pinfo[] = [];

    siteChildren.push(newPinfo('Area'
      , [
        newStringValue(this.decimalPipe.transform(this.propertyInfo[0].properties[0].details.geography.area, '1.2-2') + ' sq m (+/- 5%)')
      ]
      , []));

    siteChildren.push(newPinfo('Elevation'
      , [
        newStringValue(this.decimalPipe.transform(this.propertyInfo[0].properties[0].details.geography.elevation, '1.2-2') + ' m above sea level')
      ]
      , []));

    siteChildren.push(newPinfo('Latitude, Longitude'
      , [
        newStringValue(this.decimalPipe.transform(this.propertyInfo[0].properties[0].details.geography.latitude, '1.5-5') + '&deg; N, '
          + this.decimalPipe.transform(this.propertyInfo[0].properties[0].details.geography.longitude, '1.5-5') + '&deg; W')
      ]
      , []));

    siteChildren.push(newPinfo('Legal Plan'
      , [newStringValue('Accessible using the Legal Plan search in the Geotools toolbox')]
      // , this.propertyInfo[0].properties[0].details.documents.legalPlans.map(
      //   plan => newLinkValue(this.getLegalPlansUrl(plan.fileName), plan.fileName + ' (' + plan.type + ')')
      // )
      , []));

    if (this.propertyInfo[0].properties[0].details.ownership.propertyOwners === null) {
      siteChildren.push(newPinfo('Property Type'
        , [newStringValue('')]
        , []));
    } else {
      siteChildren.push(newPinfo('Property Type'
        , [newStringValue(this.propertyInfo[0].properties[0].details.ownership.propertyType)]
        , []));
    }

    siteChildren.push(newPinfo('Vacant Property'
      , [newStringValue(this.propertyInfo[0].properties[0].details.ownership.isVacant === true ? yes : no)]
      , []));

    siteChildren.push(newPinfo('Transit Oriented Area'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.transitOrientedArea === null ? 'N/A' : this.propertyInfo[0].properties[0].details.spatialRelations.transitOrientedArea)]
      , []));

    if (siteChildren.length > 0) {
      pi.push(newPinfo('SiteKey', [newStringValue('Site')], siteChildren));
    }



    if (this.propertyInfo[0].properties[0].details.ownership.propertyOwners === null) {
      // Ownership
      const ownershipChildren: Pinfo[] = [];

      ownershipChildren.push(newPinfo('Owner Type'
        , [newStringValue(this.propertyInfo[0].properties[0].details.ownership.propertyType)]
        , []));

      ownershipChildren.push(newPinfo('Leased Property'
        , [newStringValue(this.propertyInfo[0].properties[0].details.ownership.isLeased === true ? yes : no)]
        , []));

      if (ownershipChildren.length > 0) {
        pi.push(newPinfo('OwnershipKey', [newStringValue('Ownership')], ownershipChildren));
      }

    } else {
      // Owner
      this.propertyInfo[0].properties[0].details.ownership.propertyOwners.forEach((owner: PropertyOwner, index: number) => {

        const ownerChildren: Pinfo[] = [];

        ownerChildren.push(newPinfo('Name'
          , [newStringValue(owner.name)]
          , []));

        ownerChildren.push(newPinfo('Owner Type'
          , [newStringValue(owner.ownerType)]
          , []));

        ownerChildren.push(newPinfo('Owner Address'
          , [newStringValue(this.getAddress(owner))]
          , []));

        ownerChildren.push(newPinfo('Primary Class'
          , [newStringValue(owner.primaryClass)]
          , []));

        if (ownerChildren.length > 0) {
          pi.push(newPinfo(`OwnerKey${index}`, [newStringValue(`Owner ${index + 1}`)], ownerChildren));
        }

      });

    }


    // Civic
    const civicChildren: Pinfo[] = [];

    civicChildren.push(newPinfo('Zoning'
      , this.propertyInfo[0].properties[0].details.spatialRelations.zonings.map(z => newStringValue(z.zoning))
      , []));

    civicChildren.push(newPinfo('DPA'
      , this.propertyInfo[0].properties[0].details.spatialRelations.dpAs.length === 0
        ? [newStringValue('Not subject to a DPA')]
        : this.propertyInfo[0].properties[0].details.spatialRelations.dpAs.map(dpa => {
          if (dpa.subtype === null) {
            return newStringValue(dpa.name);
          } else {
            return newStringValue(`${dpa.name}: ${dpa.subtype}`);
          }
        })
      , []));

    civicChildren.push(newPinfo('ISMP Watershed'
      , this.propertyInfo[0].properties[0].details.spatialRelations.ismpZone.map(zone => newStringValue(zone))
      , []));

    civicChildren.push(newPinfo('Snow Load Zone'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.snowLoad)]
      , []));

    civicChildren.push(newPinfo('Neighbourhood'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.neighbourhood)]
      , []));

    if (civicChildren.length > 0) {
      pi.push(newPinfo('CivicKey', [newStringValue('Civic')], civicChildren));
    }


    // Building
    const buildingChildren: Pinfo[] = [];

    if (this.propertyInfo[0].properties[0].details.building) {

      buildingChildren.push(newPinfo('Building Name'
        , [newStringValue(this.propertyInfo[0].properties[0].details.building.name)]
        , []));

      const yearBuilt = this.propertyInfo[0].properties[0].details.building.yearBuilt
        ? this.propertyInfo[0].properties[0].details.building.yearBuilt.toString()
        : 'No date information found';
      buildingChildren.push(newPinfo('Year Built'
        , [newStringValue(yearBuilt)]
        , []));

      buildingChildren.push(newPinfo('Type'
        , [newStringValue(this.propertyInfo[0].properties[0].details.building.type)]
        , []));

      buildingChildren.push(newPinfo('Subtype'
        , [newStringValue(this.propertyInfo[0].properties[0].details.building.subtype)]
        , []));

    } else {

      buildingChildren.push(newPinfo('Building Name'
        , [newStringValue('No building found')]
        , []));

      buildingChildren.push(newPinfo('Year Built'
        , [newStringValue('No data information found')]
        , []));

      buildingChildren.push(newPinfo('Type'
        , [newStringValue(na)]
        , []));

      buildingChildren.push(newPinfo('Subtype'
        , [newStringValue(na)]
        , []));
    }

    if (buildingChildren.length > 0) {
      pi.push(newPinfo('BuildingKey', [newStringValue('Building')], buildingChildren));
    }


    // Heritage
    const heritageChildren: Pinfo[] = [];

    heritageChildren.push(newPinfo('Heritage Status'
      , [newStringValue(this.getHeritageStatus(this.propertyInfo[0].properties[0].details.building?.heritageStatus))]
      , []));

    if (this.propertyInfo[0].properties[0].details.building?.heritageStatus) {
      heritageChildren.push(newPinfo('Legally Protected'
        , [newStringValue(
          this.propertyInfo[0].properties[0].details.building?.heritageLegallyProtected === true ? 'Yes' : 'No'
        )]
        , []));

      heritageChildren.push(newPinfo('Heritage Registry'
        , [this.propertyInfo[0].properties[0].details.building?.heritageRegistry === true
          ? newLinkValue(this.appSettings['appConfig.heritageRegistryURL'], 'This house is on the Heritage Registry')
          : newLinkValue(this.appSettings['appConfig.heritageRegistryURL'], 'This house is not on the Heritage Registry')]
        , []));
      if (this.propertyInfo[0].properties[0].details.building?.heritageInventory) {
        heritageChildren.push(newPinfo('Heritage Inventory'
          , [newLinkValue(this.getHeritageInventoryUrl(this.propertyInfo[0].properties[0].details.building?.heritageInventory), this.getHeritageInventory(this.propertyInfo[0].properties[0].details.building?.heritageInventory))]
          , []));
      }

    }

    if (heritageChildren.length > 0) {
      pi.push(newPinfo('HeritageKey', [newStringValue('Heritage')], heritageChildren));
    }


    // Services
    const servicesChildren: Pinfo[] = [];

    if (this.propertyInfo[0].properties[0].details.documents.sanitaryDrawings
      && this.propertyInfo[0].properties[0].details.documents.sanitaryDrawings.length > 0) {
      const sani = this.propertyInfo[0].properties[0].details.documents.sanitaryDrawings.map(sani => {
        return newButtonValue(sani.drawingNumber, sani.drawingType, `${sani.drawingNumber} (${sani.drawingYear} ${sani.drawingType})`)
      });
      servicesChildren.push(newPinfo('Sanitary Connection'
        , sani
        , []));
    } else {
      servicesChildren.push(newPinfo('Sanitary Connection'
        , [newStringValue('No drawing information found')]
        , []));

    }

    if (this.propertyInfo[0].properties[0].details.documents.stormDrawings
      && this.propertyInfo[0].properties[0].details.documents.stormDrawings.length > 0) {
      const storm = this.propertyInfo[0].properties[0].details.documents.stormDrawings.map(storm => {
        return newButtonValue(storm.drawingNumber, storm.drawingType, `${storm.drawingNumber} (${storm.drawingYear} ${storm.drawingType})`)
      });
      servicesChildren.push(newPinfo('Storm Connection'
        , storm
        , []));
    } else {
      servicesChildren.push(newPinfo('Storm Connection'
        , [newStringValue('No drawing information found')]
        , []));
    }

    if (this.propertyInfo[0].properties[0].details.documents.waterDrawings
      && this.propertyInfo[0].properties[0].details.documents.waterDrawings.length > 0) {
      const water = this.propertyInfo[0].properties[0].details.documents.waterDrawings.map(water => {
        return newButtonValue(water.drawingNumber, water.drawingType, `${water.drawingNumber} (${water.drawingYear} ${water.drawingType})`)
      });
      servicesChildren.push(newPinfo('Water Connection'
        , water
        , []));
    } else {
      servicesChildren.push(newPinfo('Water Connection'
        , [newStringValue('No drawing information found')]
        , []));
    }

    servicesChildren.push(newPinfo('Service Connections Report'
      , [newLinkValue(`/serviceconnections?dblink=${this.propertyInfo[0].properties[0].details.identification.dbLink}&opacity=${this.opacity}`, 'Open')]
      , []));

    servicesChildren.push(newPinfo('Next Garbage Days'
      , this.propertyInfo[0].properties[0].details.spatialRelations.garbageDays.map(day => newStringValue(day))
      , []));

    if (servicesChildren.length > 0) {
      pi.push(newPinfo('ServicesKey', [newStringValue('Services')], servicesChildren));
    }


    // Value
    const valueChildren: Pinfo[] = [];

    if (this.propertyInfo[0].properties[0].details.assessment) {
      const land = [newStringValue(this.currencyPipe.transform(this.propertyInfo[0].properties[0].details.assessment.landValue, '', 'symbol', '4.0-0'))]
      if (this.propertyInfo[0].properties[0].details.assessment.taxConsolidation) {
        land.push(newStringValue('(TAX CONSOLIDATION)'))
      }
      valueChildren.push(newPinfo('Land'
        , land
        , []));
    } else {
      valueChildren.push(newPinfo('Land'
        , [newStringValue('This property has not been assessed.')]
        , []));
    }

    if (this.propertyInfo[0].properties[0].details.assessment
      && this.propertyInfo[0].properties[0].details.assessment.buildingValue > 0
    ) {
      const building = [newStringValue(this.currencyPipe.transform(this.propertyInfo[0].properties[0].details.assessment.buildingValue, '', 'symbol', '4.0-0'))]
      if (this.propertyInfo[0].properties[0].details.assessment.taxConsolidation) {
        building.push(newStringValue('(TAX CONSOLIDATION)'))
      }
      valueChildren.push(newPinfo('Building'
        , building
        , []));
    } else {
      valueChildren.push(newPinfo('Building'
        , [newStringValue('This property has not been assessed.')]
        , []));
    }

    if (this.propertyInfo[0].properties[0].details.assessment) {
      const total = [newStringValue(this.currencyPipe.transform(this.propertyInfo[0].properties[0].details.assessment.totalValue, '', 'symbol', '4.0-0'))]
      if (this.propertyInfo[0].properties[0].details.assessment.taxConsolidation) {
        total.push(newStringValue('(TAX CONSOLIDATION)'))
      }
      valueChildren.push(newPinfo('Total'
        , total
        , []));
    } else {
      valueChildren.push(newPinfo('Total'
        , [newStringValue('This property has not been assessed.')]
        , []));
    }


    if (valueChildren.length > 0) {
      pi.push(newPinfo('ValueKey', [newStringValue('Value')], valueChildren));
    }


    // Hazard Reports
    const hazardChildren: Pinfo[] = [];

    hazardChildren.push(newPinfo(''
      , this.propertyInfo[0].properties[0].details.spatialRelations.hazardReports.map(hazard => {
        return newLinkValue(hazard.url, hazard.name, `${hazard.author} (${hazard.hazardType} ${hazard.date})`);
      })
      , []));

    if (hazardChildren.length > 0) {
      pi.push(newPinfo('HazardReportsKey', [newStringValue('Hazard Reports')], hazardChildren));
    }


    // Closest DNV
    const closestDNVChildren: Pinfo[] = [];

    closestDNVChildren.push(newPinfo('Elementary School'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.elementarySchool)]
      , []));

    closestDNVChildren.push(newPinfo('Secondary School'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.secondarySchool)]
      , []));

    closestDNVChildren.push(newPinfo('Recreation Centre'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.recreationCentre)]
      , []));

    closestDNVChildren.push(newPinfo('Library'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.library)]
      , []));

    closestDNVChildren.push(newPinfo('Fire Hall'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.firehall)]
      , []));

    closestDNVChildren.push(newPinfo('Hospital'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.hospital)]
      , []));

    closestDNVChildren.push(newPinfo('Police Station'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.policeStation)]
      , []));

    closestDNVChildren.push(newPinfo('Hydrant'
      , [newStringValue(this.decimalPipe.transform(this.propertyInfo[0].properties[0].details.spatialRelations.hydrant, '1.0-0') + ' m')]
      , []));

    closestDNVChildren.push(newPinfo('Electoral Riding'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.electoralRiding)]
      , []));

    closestDNVChildren.push(newPinfo('MLA'
      , [newStringValue(this.propertyInfo[0].properties[0].details.spatialRelations.mla)]
      , []));

    if (closestDNVChildren.length > 0) {
      pi.push(newPinfo('ClosestDNVKey', [newStringValue('Closest DNV')], closestDNVChildren));
    }


    this.fullPropertyInfo = pi;
  }

  filterPropertyInfo() {
    this.filter = this.searchPropertyInformationForm.get('searchPropertyInformation').value.toLowerCase();

    if (this.filter.length === 0) {
      this.filteredPropertyInfo = this.fullPropertyInfo;
      return;
    }

    const fpi: Pinfo[] = [];
    this.fullPropertyInfo.forEach(pi => {
      if (pi.text.includes(this.filter)) { // the filter match a section header so return the entire section 'as-is'
        fpi.push(pi);
      } else {
        const children = pi.children.filter(child => child.text.includes(this.filter));
        if (children.length > 0) {
          fpi.push(Object.assign({}, pi, { children: children }));
        }

        /*         pi.children.forEach(pc => {
                  if (pc.children && pc.children.length > 0) {
                    const pchildren = pc.children.filter(pcc => {
                      if (pcc.text.includes(this.filter)) {
                        return pcc;
                      }
                    });
                    if (pchildren.length > 0) {
                      const pchild = Object.assign({}, pc, { children: pchildren });
                      //  const pcobj = Object.assign({}, pi.children[0], { children: pchild });
                      fpi.push(Object.assign({}, pi, { children: [pchild] }));
                    }
                  }
                }); */
      }
    });

    this.filteredPropertyInfo = fpi;
  }

  parseCases(): void {
    const cases: Pinfo[] = [];
    if (this.energovInfo) {

      // permits
      if (this.energovInfo.energovPermits) {

        const permitsChildren: Pinfo[] = [];

        const permits = this.energovInfo.energovPermits;

        const permitLink = this.appSettings['environment.permitLink'];

        for (const p of permits) {
          const permit: Pinfo[] = [];

          permit.push(newPinfo('Work Class'
            , [
              newStringValue(p.workClass)
            ]
            , []));

          permit.push(newPinfo('Status'
            , [
              newStringValue(p.status)
            ]
            , []));

          permit.push(newPinfo('Issue Date'
            , [
              newStringValue(p.issueDate ? new DatePipe('en-CA').transform(p.issueDate, 'MMM dd yyyy') : '-')
            ]
            , []));

          permit.push(newPinfo('Link'
            , [newLinkValue(permitLink + p.permitId, p.permitNumber, null)]
            , []));

          permitsChildren.push(newPinfo(p.permitNumber, [newStringValue(p.permitNumber)], permit));

        }
        cases.push(newPinfo('PermitsKey', [newStringValue('Permits')], permitsChildren));
      }

      // code cases
      if (this.energovInfo.energovCodeCases) {

        const codeCaseChildren: Pinfo[] = [];

        const codecases = this.energovInfo.energovCodeCases;

        const ccLink = this.appSettings['environment.ccLink'];

        for (const c of codecases) {
          const codecase: Pinfo[] = [];

          codecase.push(newPinfo('Case Type'
            , [
              newStringValue(c.codeCaseType)
            ]
            , []));

          codecase.push(newPinfo('Status'
            , [
              newStringValue(c.status)
            ]
            , []));

          codecase.push(newPinfo('Opened Date'
            , [
              newStringValue(c.openedDate ? new DatePipe('en-CA').transform(c.openedDate, 'MMM dd yyyy') : '-')
            ]
            , []));

          codecase.push(newPinfo('Link'
            , [newLinkValue(ccLink + c.codeCaseId, c.codeCaseNumber, null)]
            , []));

          codeCaseChildren.push(newPinfo(c.codeCaseNumber, [newStringValue(c.codeCaseNumber)], codecase));

        }
        cases.push(newPinfo('CodeCaseKey', [newStringValue('Code Cases')], codeCaseChildren));
      }

      // business license
      if (this.energovInfo.energovBusLicenses) {

        const blChildren: Pinfo[] = [];

        const busLicenses = this.energovInfo.energovBusLicenses;

        const blLink = this.appSettings['environment.blLink'];

        for (const bl of busLicenses) {
          const buslic: Pinfo[] = [];

          buslic.push(newPinfo('DBA'
            , [
              newStringValue(bl.dBA)
            ]
            , []));

          buslic.push(newPinfo('License Year'
            , [
              newStringValue(bl.licenseYear.toString())
            ]
            , []));

          buslic.push(newPinfo('Classification'
            , [
              newStringValue(bl.classification)
            ]
            , []));

          buslic.push(newPinfo('Link'
            , [newLinkValue(blLink + bl.licenseId, bl.licenseNumber, null)]
            , []));

          blChildren.push(newPinfo(bl.licenseNumber, [newStringValue(bl.licenseNumber)], buslic));

        }
        const currentYr = new Date().getFullYear();
        cases.push(newPinfo('BusinessLicenseKey', [newStringValue(currentYr + ' Business Licenses')], blChildren));
      }

    }
    this.fullCasesInfo = cases;
  }

  closeRightPanel(): void {
    this.closePanel.emit();
  }

  getHeritageStatus(value: string): string {
    if (value) {
      return value;
    } else {
      return 'Not a Heritage Building';
    }
  }

  getHeritageInventory(value: string): string {
    if (value == '1993 REPORT') return 'Heritage Inventory 1993';
    if (value == '1997 REPORT') return 'Modern Inventory 1997';
    return '';
  }

  getHeritageInventoryUrl(value: string): string {
    if (value == '1993 REPORT') return this.appSettings['HeritageInventory1993'];
    if (value == '1997 REPORT') return this.appSettings['HeritageInventory1997'];
    return '';
  }

  getAddress(ownr: PropertyOwner): string {
    let address = ownr.address1 + '<br/>' + ownr.address2;
    if (ownr.address3) {
      address += '<br/>' + ownr.address3;
    }
    if (ownr.address4) {
      address += '<br/>' + ownr.address4;
    }
    if (ownr.address5) {
      address += '<br/>' + ownr.address5;
    }
    if (ownr.address6) {
      address += '<br/>' + ownr.address6;
    }
    return address;
  }

  setUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  openDrawing(drawingNumber: string, drawingType: string): void {
    this.openDrawingEmitter.emit({ drawNum: drawingNumber, drawType: drawingType });
  }

  backToMultiple(dblink: string): void {
    this.backToMultiEmitter.emit(dblink);
  }

  getStreetViewStatic(geography: any): string {
    this.currentLatLng = {
      lat: geography.latitude,
      lng: geography.longitude
    };
    // tslint:disable-next-line: max-line-length
    return 'https://maps.googleapis.com/maps/api/streetview?size=260x150&location=' + geography.streetViewLat + ',' + geography.streetViewLong + '&heading=' + geography.streetViewAngle + '&pitch=0&key=' + this.appSettings['environment.GoogleAPIKey'];
  }

  getLegalPlansUrl(fileName: string): string {
    return this.appSettings['environment.legalPlansUrl'] + fileName;
  }

  onClearPropSearch(): void {
    this.searchPropertyInformationForm.get('searchPropertyInformation').setValue('');
    this.filterPropertyInfo();
  }

  getTabKey(key: string): string {
    return key.replace('.', '');
  }

  countRecords(): string {
    let casesCount = 0;
    if (this.energovInfo) {
      if (this.energovInfo.energovBusLicenses) {
        casesCount = casesCount + this.energovInfo.energovBusLicenses.length;
      }

      if (this.energovInfo.energovCodeCases) {
        casesCount = casesCount + this.energovInfo.energovCodeCases.length;
      }

      if (this.energovInfo.energovPermits) {
        casesCount = casesCount + this.energovInfo.energovPermits.length;
      }

      if (this.energovInfo.energovPlans) {
        casesCount = casesCount + this.energovInfo.energovPlans.length;
      }
    }

    return casesCount.toString();
  }
}
